import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, FileText, Shield, AlertCircle, CheckCircle, ChevronDown, ChevronRight, Download, Share2, Search } from 'lucide-react';
import { PersonaSwitcher } from '../components/PersonaSwitcher';
import { MessageModal } from '../components/modals/MessageModal';
import { SocialShare } from '../components/SocialShare';
export function LegalGuidesPage() {
  const [expandedGuide, setExpandedGuide] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [currentGuideTitle, setCurrentGuideTitle] = useState('');
  const handleDownload = (guideTitle: string) => {
    setCurrentGuideTitle(guideTitle);
    setIsDownloadModalOpen(true);
    // In production, this would trigger actual PDF download
    console.log('Downloading guide:', guideTitle);
  };
  const handleShare = (guideTitle: string) => {
    setCurrentGuideTitle(guideTitle);
    setIsShareModalOpen(true);
  };
  const guides = [{
    id: 'property-documents',
    title: 'Essential Property Documents',
    category: 'Documentation',
    icon: FileText,
    summary: 'Complete checklist of documents required for plot purchase',
    content: {
      overview: 'Before purchasing any plot, ensure all legal documents are verified and in order. Missing or fraudulent documents are the primary cause of property disputes in India.',
      sections: [{
        title: 'Primary Documents',
        items: [{
          name: 'Title Deed (Sale Deed)',
          description: 'Proves ownership of the property. Must be registered with Sub-Registrar office.'
        }, {
          name: 'Encumbrance Certificate (EC)',
          description: 'Shows property is free from legal dues and liabilities for past 13-30 years.'
        }, {
          name: '7/12 Extract (Maharashtra) / Pahani (Other States)',
          description: 'Land revenue record showing ownership, cultivation details, and land type.'
        }, {
          name: 'Property Tax Receipts',
          description: 'Proof of paid property taxes for the last 3 years minimum.'
        }]
      }, {
        title: 'Conversion & Approval Documents',
        items: [{
          name: 'NA (Non-Agricultural) Order',
          description: 'Required if agricultural land is being converted for residential/commercial use.'
        }, {
          name: 'Layout Approval',
          description: 'Approved layout plan from local development authority (PMRDA, BDA, etc.).'
        }, {
          name: 'Commencement Certificate',
          description: 'Permission to start construction, issued by local municipal body.'
        }]
      }, {
        title: 'Regulatory Compliance',
        items: [{
          name: 'RERA Registration Certificate',
          description: 'Mandatory for projects with 8+ plots or 500+ sqm area. Verify on state RERA website.'
        }, {
          name: 'Occupancy Certificate (OC)',
          description: 'For completed projects, certifies building is fit for occupation.'
        }, {
          name: 'Building Plan Approval',
          description: 'Sanctioned building plan from municipal corporation.'
        }]
      }],
      warning: 'Always verify original documents. Do not rely on photocopies. Engage a property lawyer for document verification.'
    }
  }, {
    id: 'due-diligence',
    title: 'Legal Due Diligence Process',
    category: 'Verification',
    icon: Shield,
    summary: 'Step-by-step guide to verify property legality',
    content: {
      overview: 'Legal due diligence is crucial to avoid future disputes. Follow this systematic approach before making any payment.',
      sections: [{
        title: 'Title Verification (Most Critical)',
        items: [{
          name: 'Chain of Ownership',
          description: 'Verify unbroken ownership chain for minimum 30 years. Check all previous sale deeds.'
        }, {
          name: 'Encumbrance Check',
          description: 'Obtain EC from Sub-Registrar office. Ensure no mortgages, liens, or legal cases.'
        }, {
          name: 'Court Case Search',
          description: 'Check for pending litigation at District Court, High Court, and Supreme Court.'
        }, {
          name: 'Revenue Records Verification',
          description: 'Match 7/12 extract with sale deed. Verify survey numbers and plot boundaries.'
        }]
      }, {
        title: 'Physical Verification',
        items: [{
          name: 'Site Visit',
          description: 'Visit plot multiple times. Check for encroachments, unauthorized constructions.'
        }, {
          name: 'Boundary Verification',
          description: 'Hire licensed surveyor to verify plot dimensions and boundaries match documents.'
        }, {
          name: 'Access & Amenities',
          description: 'Verify road access, water supply, electricity connection availability.'
        }]
      }, {
        title: 'Regulatory Compliance Check',
        items: [{
          name: 'Zoning Verification',
          description: 'Confirm plot falls in residential/commercial zone as per master plan.'
        }, {
          name: 'RERA Verification',
          description: 'For projects, verify RERA registration on official state website.'
        }, {
          name: 'NOC Collection',
          description: 'Obtain NOCs from relevant authorities: Fire, Pollution Control, Airport Authority (if applicable).'
        }]
      }],
      warning: 'Hire a property lawyer for complete due diligence. Cost: ₹10,000-50,000 depending on complexity. Worth the investment.'
    }
  }, {
    id: 'registration-process',
    title: 'Property Registration Guide',
    category: 'Transaction',
    icon: FileText,
    summary: 'Complete process of registering property in India',
    content: {
      overview: 'Property registration is mandatory under Indian Registration Act, 1908. Unregistered sale deeds have no legal validity.',
      sections: [{
        title: 'Pre-Registration Steps',
        items: [{
          name: 'Draft Sale Deed',
          description: 'Prepare sale deed with all property details, buyer-seller information, and consideration amount.'
        }, {
          name: 'Stamp Duty Payment',
          description: 'Pay stamp duty (varies by state: 5-7% of property value). Can be paid online or via franking.'
        }, {
          name: 'Document Preparation',
          description: 'Collect: Sale deed, ID proofs, address proofs, PAN cards, property documents, NOC (if applicable).'
        }]
      }, {
        title: 'Registration Day Process',
        items: [{
          name: 'Sub-Registrar Office Visit',
          description: 'Both buyer and seller must be present with 2 witnesses each.'
        }, {
          name: 'Document Submission',
          description: 'Submit all documents, photos, and fingerprints. Pay registration fee (1% of property value).'
        }, {
          name: 'Biometric Verification',
          description: 'Biometric verification of buyer, seller, and witnesses.'
        }, {
          name: 'Document Signing',
          description: 'All parties sign the sale deed in presence of Sub-Registrar.'
        }]
      }, {
        title: 'Post-Registration',
        items: [{
          name: 'Collect Registered Deed',
          description: 'Collect registered sale deed within 7-15 days. Keep original safely.'
        }, {
          name: 'Mutation of Property',
          description: 'Apply for mutation in revenue records. Update 7/12 extract with your name.'
        }, {
          name: 'Update Utility Connections',
          description: 'Transfer electricity, water connections to your name.'
        }]
      }],
      warning: 'Registration must be done within 4 months of sale deed execution. Delay attracts penalty.'
    }
  }, {
    id: 'taxes-charges',
    title: 'Taxes & Charges Breakdown',
    category: 'Financial',
    icon: FileText,
    summary: 'Complete breakdown of all costs in property transaction',
    content: {
      overview: 'Property purchase involves multiple taxes and charges beyond the plot price. Budget for 8-10% additional cost.',
      sections: [{
        title: 'Government Taxes',
        items: [{
          name: 'Stamp Duty',
          description: 'State-specific: 5-7% of property value. Maharashtra: 6% (male), 5% (female). Check state rates.'
        }, {
          name: 'Registration Charges',
          description: '1% of property value (capped at ₹30,000 in most states).'
        }, {
          name: 'GST (if applicable)',
          description: '5% GST on under-construction properties. Not applicable for resale/land.'
        }]
      }, {
        title: 'Professional Fees',
        items: [{
          name: 'Legal Fees',
          description: '₹10,000-50,000 for document verification and legal opinion.'
        }, {
          name: 'Brokerage',
          description: 'Direct transactions eliminate brokerage fees. Traditional market: 1-2% of property value.'
        }, {
          name: 'Survey Charges',
          description: '₹5,000-15,000 for professional boundary survey.'
        }]
      }, {
        title: 'Post-Purchase Costs',
        items: [{
          name: 'Property Tax',
          description: 'Annual tax to municipal corporation. Varies by location and property size.'
        }, {
          name: 'Maintenance Charges',
          description: 'For gated communities: ₹2-5 per sqft per month.'
        }]
      }],
      warning: 'Budget Example: ₹50L plot = ₹3L stamp duty + ₹50K registration + ₹25K legal + ₹1L brokerage = ₹53.75L total.'
    }
  }, {
    id: 'nri-guidelines',
    title: 'NRI Property Purchase Guide',
    category: 'NRI Specific',
    icon: FileText,
    summary: 'Special guidelines for Non-Resident Indians',
    content: {
      overview: 'NRIs can purchase residential/commercial property in India but must follow FEMA regulations and specific procedures.',
      sections: [{
        title: 'FEMA Compliance',
        items: [{
          name: 'Allowed Property Types',
          description: 'Can buy: Residential, commercial properties. Cannot buy: Agricultural land, plantation, farmhouse.'
        }, {
          name: 'Payment Method',
          description: 'Must use NRE/NRO account or direct remittance from abroad. Keep FIRC (Foreign Inward Remittance Certificate).'
        }, {
          name: 'Repatriation Rules',
          description: 'Can repatriate up to USD 1 million per financial year. Need RBI approval for higher amounts.'
        }]
      }, {
        title: 'Documentation Requirements',
        items: [{
          name: 'Power of Attorney (POA)',
          description: 'If not present in India, execute POA. Must be notarized and apostilled in country of residence.'
        }, {
          name: 'PAN Card',
          description: 'Mandatory for property purchase. Apply online if not available.'
        }, {
          name: 'Address Proof',
          description: 'Passport, OCI card, or Indian address proof acceptable.'
        }]
      }, {
        title: 'Tax Implications',
        items: [{
          name: 'TDS on Purchase',
          description: '20% TDS + surcharge on property value if buying from resident Indian. 22.88% effective rate.'
        }, {
          name: 'Capital Gains Tax',
          description: 'On sale: LTCG 20% (held >2 years), STCG as per slab (held <2 years).'
        }, {
          name: 'Double Taxation Avoidance',
          description: 'Check DTAA between India and country of residence. May get tax credit.'
        }]
      }],
      warning: 'Consult CA specializing in NRI taxation. Engage property lawyer in India for POA execution and registration.'
    }
  }, {
    id: 'common-frauds',
    title: 'Common Property Frauds & Red Flags',
    category: 'Safety',
    icon: AlertCircle,
    summary: 'How to identify and avoid property scams',
    content: {
      overview: 'Property fraud is common in India. Be vigilant and follow due diligence to avoid becoming a victim.',
      sections: [{
        title: 'Common Fraud Types',
        items: [{
          name: 'Duplicate Sale',
          description: 'Same property sold to multiple buyers. Check EC and visit Sub-Registrar office.'
        }, {
          name: 'Forged Documents',
          description: 'Fake sale deeds, NOCs. Always verify originals from issuing authority.'
        }, {
          name: 'Unauthorized Colonies',
          description: 'Illegal layouts without approval. Verify layout approval from development authority.'
        }, {
          name: 'Disputed Property',
          description: 'Property under litigation. Check court records and EC thoroughly.'
        }]
      }, {
        title: 'Red Flags to Watch',
        items: [{
          name: 'Unusually Low Price',
          description: 'If price is 30%+ below market rate, investigate thoroughly. Too good to be true usually is.'
        }, {
          name: 'Pressure to Pay Quickly',
          description: 'Seller rushing transaction, asking for advance without documents. Major red flag.'
        }, {
          name: 'Only Photocopies Shown',
          description: 'Seller not showing original documents. Insist on seeing originals.'
        }, {
          name: 'Seller Avoiding Registration',
          description: 'Suggesting unregistered sale or power of attorney sale. Never agree.'
        }]
      }, {
        title: 'Protection Measures',
        items: [{
          name: 'Hire Property Lawyer',
          description: 'Non-negotiable. Lawyer will verify all documents and title.'
        }, {
          name: 'Physical Verification',
          description: 'Visit property multiple times, talk to neighbors, check for disputes.'
        }, {
          name: 'Payment Through Banking',
          description: 'Never pay cash. Use cheque/NEFT with clear property details in narration.'
        }, {
          name: 'Title Insurance',
          description: 'Consider title insurance (available in major cities) for additional protection.'
        }]
      }],
      warning: 'If you suspect fraud, report to local police and cyber crime cell immediately. Do not confront fraudsters alone.'
    }
  }];
  const filteredGuides = guides.filter(guide => {
    const matchesSearch = guide.title.toLowerCase().includes(searchQuery.toLowerCase()) || guide.summary.toLowerCase().includes(searchQuery.toLowerCase()) || guide.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || guide.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  const categories = ['All', 'Documentation', 'Verification', 'Transaction', 'Financial', 'NRI Specific', 'Safety'];
  return <div className="min-h-screen bg-[#f5f5f7] font-sans text-[#1f2933]" data-id="element-4515">
      {/* Header */}
      <header className="bg-white border-b border-gray-300 py-4 px-6 md:px-12 flex justify-between items-center sticky top-0 z-10" data-id="element-4516">
        <Link to="/" className="flex items-center gap-2" data-id="element-4517">
          <div className="w-8 h-8 bg-[#1f2933] rounded-sm" data-id="element-4518"></div>
          <span className="font-bold text-xl tracking-tight" data-id="element-4519">
            Citiplots.com
          </span>
        </Link>
        <nav className="hidden md:flex gap-8 text-sm font-medium text-gray-600" data-id="element-4520">
          <Link to="/" data-id="element-4521">Buy Plots</Link>
          <Link to="/create-listing" data-id="element-4522">Sell Land</Link>
          <Link to="/dashboard" data-id="element-4523">Dashboard</Link>
          <Link to="/legal-guides" className="text-[#1f2933]" data-id="element-4524">
            Legal Guides
          </Link>
        </nav>
        <div className="flex items-center gap-4" data-id="element-4525">
          <PersonaSwitcher data-id="element-4526" />
          <Link to="/create-listing" className="px-4 py-2 bg-[#1f2933] text-white text-sm font-bold hover:bg-gray-800" data-id="element-4527">
            Post Listing
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-white border-b border-gray-300 py-12 px-6 md:px-12" data-id="element-4528">
        <div className="max-w-4xl mx-auto text-center" data-id="element-4529">
          <div className="flex justify-center mb-4" data-id="element-4530">
            <BookOpen className="w-12 h-12 text-gray-400" data-id="element-4531" />
          </div>
          <h1 className="text-4xl font-bold mb-4 tracking-tight" data-id="element-4532">
            Legal Guides for Plot Transactions
          </h1>
          <p className="text-lg text-gray-500 mb-8 font-mono" data-id="element-4533">
            Comprehensive guides to navigate property law, documentation, and
            safe transactions in India.
          </p>

          {/* Search */}
          <div className="max-w-2xl mx-auto relative" data-id="element-4534">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-4535" />
            <input type="text" placeholder="Search guides..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500 font-mono text-sm" data-id="element-4536" />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-12" data-id="element-4537">
        {/* Category Filter - NOW WORKING */}
        <div className="flex flex-wrap gap-2 mb-8" data-id="element-4538">
          <span className="text-sm font-mono text-gray-500" data-id="element-4539">Categories:</span>
          {categories.map(cat => <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-3 py-1 text-xs font-bold border ${selectedCategory === cat ? 'border-[#1f2933] bg-gray-50' : 'border-gray-300 bg-white hover:bg-gray-50'}`} data-id="element-4540">
              {cat}
            </button>)}
        </div>

        {/* Guides List */}
        <div className="space-y-6" data-id="element-4541">
          {filteredGuides.map(guide => {
          const Icon = guide.icon;
          const isExpanded = expandedGuide === guide.id;
          return <div key={guide.id} className="bg-white border border-gray-300 overflow-hidden" data-id="element-4542">
                {/* Guide Header */}
                <button onClick={() => setExpandedGuide(isExpanded ? null : guide.id)} className="w-full p-6 flex items-start gap-4 hover:bg-gray-50 transition-colors text-left" data-id="element-4543">
                  <div className="p-2 bg-gray-100 rounded" data-id="element-4544">
                    <Icon className="w-6 h-6 text-gray-600" data-id="element-4545" />
                  </div>
                  <div className="flex-1" data-id="element-4546">
                    <div className="flex items-start justify-between gap-4" data-id="element-4547">
                      <div data-id="element-4548">
                        <span className="inline-block px-2 py-0.5 text-xs font-bold bg-gray-100 text-gray-600 rounded mb-2" data-id="element-4549">
                          {guide.category}
                        </span>
                        <h2 className="text-xl font-bold mb-2" data-id="element-4550">
                          {guide.title}
                        </h2>
                        <p className="text-sm text-gray-600" data-id="element-4551">{guide.summary}</p>
                      </div>
                      {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-4552" /> : <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-4553" />}
                    </div>
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && <div className="px-6 pb-6 border-t border-gray-200 bg-gray-50" data-id="element-4554">
                    <div className="pt-6 space-y-8" data-id="element-4555">
                      {/* Overview */}
                      <div data-id="element-4556">
                        <h3 className="text-sm font-bold text-gray-500 uppercase mb-3 font-mono" data-id="element-4557">
                          Overview
                        </h3>
                        <p className="text-sm text-gray-700 leading-relaxed" data-id="element-4558">
                          {guide.content.overview}
                        </p>
                      </div>

                      {/* Sections */}
                      {guide.content.sections.map((section, idx) => <div key={idx} data-id="element-4559">
                          <h3 className="text-sm font-bold text-gray-500 uppercase mb-4 font-mono" data-id="element-4560">
                            {section.title}
                          </h3>
                          <div className="space-y-4" data-id="element-4561">
                            {section.items.map((item, itemIdx) => <div key={itemIdx} className="bg-white border border-gray-200 p-4" data-id="element-4562">
                                <div className="flex items-start gap-3" data-id="element-4563">
                                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" data-id="element-4564" />
                                  <div data-id="element-4565">
                                    <h4 className="font-bold text-sm mb-1" data-id="element-4566">
                                      {item.name}
                                    </h4>
                                    <p className="text-sm text-gray-600" data-id="element-4567">
                                      {item.description}
                                    </p>
                                  </div>
                                </div>
                              </div>)}
                          </div>
                        </div>)}

                      {/* Warning */}
                      {guide.content.warning && <div className="bg-yellow-50 border border-yellow-200 p-4 flex items-start gap-3" data-id="element-4568">
                          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-4569" />
                          <div data-id="element-4570">
                            <h4 className="font-bold text-sm text-yellow-900 mb-1" data-id="element-4571">
                              Important Note
                            </h4>
                            <p className="text-sm text-yellow-800" data-id="element-4572">
                              {guide.content.warning}
                            </p>
                          </div>
                        </div>}

                      {/* Actions - NOW WORKING */}
                      <div className="flex gap-3 pt-4 border-t border-gray-200" data-id="element-4573">
                        <button onClick={() => handleDownload(guide.title)} className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white text-sm font-bold hover:bg-gray-50" data-id="element-4574">
                          <Download className="w-4 h-4" data-id="element-4575" /> Download PDF
                        </button>
                        <button onClick={() => handleShare(guide.title)} className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white text-sm font-bold hover:bg-gray-50" data-id="element-4576">
                          <Share2 className="w-4 h-4" data-id="element-4577" /> Share Guide
                        </button>
                      </div>
                    </div>
                  </div>}
              </div>;
        })}
        </div>

        {/* No Results */}
        {filteredGuides.length === 0 && <div className="text-center py-16 border-2 border-dashed border-gray-300 bg-gray-50" data-id="element-4578">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-4579" />
            <h3 className="font-bold text-gray-500 mb-2" data-id="element-4580">No guides found</h3>
            <p className="text-sm text-gray-400 font-mono" data-id="element-4581">
              Try adjusting your search query or category filter
            </p>
          </div>}

        {/* Disclaimer */}
        <div className="mt-12 p-6 bg-white border border-gray-300" data-id="element-4582">
          <h3 className="font-bold mb-3 flex items-center gap-2" data-id="element-4583">
            <AlertCircle className="w-5 h-5 text-gray-500" data-id="element-4584" />
            Legal Disclaimer
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-4585">
            These guides are for informational purposes only and do not
            constitute legal advice. Property laws vary by state and are subject
            to change. Always consult a qualified property lawyer before making
            any property transaction. Citiplots.com is not liable for any
            decisions made based on this information.
          </p>
        </div>
      </main>

      {/* Download Modal */}
      <MessageModal isOpen={isDownloadModalOpen} onClose={() => setIsDownloadModalOpen(false)} title="Downloading Guide" message={`Preparing PDF for "${currentGuideTitle}". In production, this would download a comprehensive PDF guide.`} type="success" data-id="element-4586" />

      {/* Share Modal */}
      {isShareModalOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-4587">
          <div className="bg-white border border-gray-300 max-w-md w-full p-6" data-id="element-4588">
            <h3 className="font-bold text-lg mb-4" data-id="element-4589">Share Guide</h3>
            <p className="text-sm text-gray-600 mb-4" data-id="element-4590">{currentGuideTitle}</p>
            <SocialShare url={window.location.href} title={currentGuideTitle} description="Legal guide from Citiplots.com" data-id="element-4591" />
            <button onClick={() => setIsShareModalOpen(false)} className="w-full mt-4 py-2 border border-gray-300 text-sm font-bold hover:bg-gray-50" data-id="element-4592">
              Close
            </button>
          </div>
        </div>}
    </div>;
}